import React, { createContext, useContext, ReactNode, useCallback } from 'react';
import { useUser } from './UserContext';
import { CreditService } from '../lib/credits';
import { UnifiedCreditService } from '../lib/unified-credit-service';

interface SubscriptionContextType {
  hasSubscriptionPlan: () => boolean;
  updateSubscriptionPlan: (plan: string) => Promise<void>;
  updateCredits: (amount: number) => Promise<void>;
  refreshUserCredits: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user, updateUserProfile, refreshUserProfile, hasInitialLoad } = useUser();

  const hasSubscriptionPlan = useCallback(() => {
    
    // Wait for initial load to complete before checking subscriptions
    if (!hasInitialLoad) {
      return false;
    }
    
    if (!user) {
      return false;
    }

    // Check if user object has necessary fields (might be loading)
    if (!('subscription_plan' in user)) {
      return false;
    }

    // First check if user is a team member - team members always have access via organization subscription
    const isTeamMember = !!(user.organization_id && user.team_role);
    if (isTeamMember) {
      return true;
    }

    // For individual users, check for valid subscription plan (exclude 'pending' state)
    const hasIndividualPlan = !!(
      user.subscription_plan && 
      user.subscription_plan !== 'none' &&
      user.subscription_plan !== 'pending'
    );

    // Also check if user belongs to organization (fallback check)
    const belongsToOrganization = !!(user.organization_id);
    
    return hasIndividualPlan || belongsToOrganization;
  }, [user, hasInitialLoad]);

  const updateSubscriptionPlan = useCallback(async (plan: string) => {
    if (!user) return;

    try {

      // Update via CreditService
      await CreditService.updateSubscriptionPlan(plan);

      // Update unified credit service
      await UnifiedCreditService.updateAfterTransaction(user.id, 'subscription_changed');

      // Refresh user profile to get updated data
      await refreshUserProfile();
      
    } catch (error) {
      console.error('Error updating subscription plan:', error);
      throw error;
    }
  }, [user, refreshUserProfile]);

  const updateCredits = useCallback(async (amount: number) => {
    if (!user) return;

    try {
      const newBalance = await CreditService.addCredits(
        amount,
        'addition',
        'Manual credit update'
      );
      
      await UnifiedCreditService.updateAfterTransaction(user.id, 'credit_added');
      
      // Refresh user to get updated credits
      await refreshUserProfile();
    } catch (error) {
      console.error('Error updating credits:', error);
      throw error;
    }
  }, [user, refreshUserProfile]);

  const refreshUserCredits = useCallback(async () => {
    if (!user?.id) {
      return;
    }

    try {
      
      const creditData = await UnifiedCreditService.getSimpleCreditInfo(user.id, true);
      
      await updateUserProfile({ credits: creditData.credits });
      
    } catch (error) {
      console.error('Error refreshing credits:', error);
    }
  }, [user, updateUserProfile]);

  return (
    <SubscriptionContext.Provider value={{
      hasSubscriptionPlan,
      updateSubscriptionPlan,
      updateCredits,
      refreshUserCredits
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}